import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import '../../styles/admin.css';
import './AdminProfesorDetallePage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const AdminProfesorDetalleInner = () => {
  const { profesorId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();

  // Verificar permisos
  const hasViewProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_PROFESORES), [user]);
  const hasEditProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_PROFESORES), [user]);
  const hasDeleteProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.ELIMINAR_PROFESORES), [user]);

  useEffect(() => {
    if (user && !hasViewProfesores) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para ver este profesor.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewProfesores, navigate, notify]);

  const [profesor, setProfesor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(false);

  // Estados de edición
  const [editState, setEditState] = useState({
    Nombre: { value: '', editing: false },
    apellido: { value: '', editing: false },
    Especialidad: { value: '', editing: false },
    Descripcion: { value: '', editing: false },
    Foto: { value: '', editing: false },
  });

  const fetchProfesor = useCallback(async () => {
    if (!token || !profesorId) return;
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl(`/api/profesores/${profesorId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`No se pudo cargar el profesor (${response.status})`);
      }

      const data = await response.json();
      const profesorData = data.profesor || data;
      setProfesor(profesorData);

      setEditState({
        Nombre: { value: profesorData.Nombre || '', editing: false },
        apellido: { value: profesorData.apellido || '', editing: false },
        Especialidad: { value: profesorData.Especialidad || '', editing: false },
        Descripcion: { value: profesorData.Descripcion || '', editing: false },
        Foto: { value: profesorData.Foto || '', editing: false },
      });
    } catch (error) {
      logError('[AdminProfesorDetallePage] Error fetching profesor:', error);
      notify({
        title: 'Error al cargar el profesor',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [profesorId, notify, token]);

  useEffect(() => {
    fetchProfesor();
  }, [fetchProfesor]);

  const updateProfesor = useCallback(async (updates) => {
    if (!token || !profesorId) return;
    setPending(true);
    try {
      const response = await fetch(buildApiUrl(`/api/profesores/${profesorId}`), {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al actualizar el profesor');
      }

      await fetchProfesor();
      return true;
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
      throw error;
    } finally {
      setPending(false);
    }
  }, [profesorId, token, notify, fetchProfesor]);

  const handleSaveField = useCallback(async (field, value) => {
    const updateData = {};
    
    switch (field) {
      case 'Nombre':
        updateData.Nombre = value;
        break;
      case 'apellido':
        updateData.apellido = value;
        break;
      case 'Especialidad':
        updateData.Especialidad = value;
        break;
      case 'Descripcion':
        updateData.Descripcion = value;
        break;
      case 'Foto':
        updateData.Foto = value;
        break;
      default:
        return;
    }

    try {
      await updateProfesor(updateData);
      setEditState(prev => ({
        ...prev,
        [field]: { ...prev[field], editing: false },
      }));
      notify({
        title: 'Éxito',
        message: 'Campo actualizado correctamente',
        variant: 'success',
      });
    } catch (error) {
      // Error ya manejado en updateProfesor
    }
  }, [updateProfesor, notify]);

  const handleDeleteProfesor = useCallback(async () => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este profesor?')) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/profesores/${profesorId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al eliminar el profesor');
      }

      notify({
        title: 'Éxito',
        message: 'Profesor eliminado correctamente',
        variant: 'success',
      });
      navigate('/admin/profesores');
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    }
  }, [profesorId, token, notify, navigate]);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="profesores" />
          <main className="admin-main">
            <div className="APDP-container">
              <div className="APDP-loading">
                <Spinner animation="border" />
                <p>Cargando profesor...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (!profesor) {
    return (
      <div className="admin-dashboard">
        <div className="admin-layout">
          <AdminSidebar activeKey="profesores" />
          <main className="admin-main">
            <div className="APDP-container">
              <div className="APDP-error">
                <h2>Profesor no encontrado</h2>
                <p>El profesor que buscas no existe o fue eliminado.</p>
                <Button variant="primary" onClick={() => navigate('/admin/profesores')}>
                  Volver a Profesores
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar activeKey="profesores" onNavigate={(item) => navigate(item.path)} />
        <main className="admin-main">
          <div className="APDP-container">
            {/* Header */}
            <div className="APDP-header">
              <div className="APDP-header-content">
                <button
                  type="button"
                  className="APDP-back-button"
                  onClick={() => navigate('/admin/profesores')}
                >
                  <i className="bi-arrow-left-circle" />
                  Volver
                </button>
                <h1 className="APDP-title">
                  <i className="bi-person-badge" />
                  {profesor.Nombre} {profesor.apellido}
                </h1>
                <p className="APDP-subtitle">
                  ID: {profesor.id} {profesor.Especialidad && `• ${profesor.Especialidad}`}
                </p>
              </div>
              <div className="APDP-actions">
                {hasDeleteProfesores && (
                  <button
                    type="button"
                    className="APDP-action-btn APDP-action-btn-danger"
                    onClick={handleDeleteProfesor}
                    disabled={pending}
                  >
                    <i className="bi-trash" />
                    Eliminar
                  </button>
                )}
              </div>
            </div>

            {/* Profesor Card */}
            <div className="APDP-profesor-card">
              {/* Image Section */}
              <div className="APDP-profesor-image-section">
                {profesor.Foto ? (
                  <img
                    src={resolveImageUrl(profesor.Foto)}
                    alt={`${profesor.Nombre} ${profesor.apellido}`}
                    className="APDP-profesor-image"
                  />
                ) : (
                  <div className="APDP-profesor-image-placeholder">
                    <i className="bi-person-badge" />
                    <p>Sin foto</p>
                  </div>
                )}
                {hasEditProfesores && (
                  <div className="APDP-profesor-image-overlay">
                    <button
                      type="button"
                      className="APDP-profesor-image-button"
                      onClick={() => {
                        setImageUrl(profesor.Foto || '');
                        setShowImageModal(true);
                      }}
                    >
                      <i className="bi-camera-video" />
                      {profesor.Foto ? 'Cambiar foto' : 'Agregar foto'}
                    </button>
                  </div>
                )}
              </div>

              {/* Content Section */}
              <div className="APDP-profesor-content">
                {/* Nombre */}
                <div className="APDP-field-item">
                  <div className="APDP-field-header">
                    <label className="APDP-field-label">Nombre *</label>
                    {hasEditProfesores && !editState.Nombre.editing && (
                      <button
                        type="button"
                        className="APDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, Nombre: { ...prev.Nombre, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.Nombre.editing ? (
                    <div className="APDP-field-edit">
                      <input
                        type="text"
                        className="APDP-field-input"
                        value={editState.Nombre.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          Nombre: { ...prev.Nombre, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('Nombre', editState.Nombre.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveField('Nombre', editState.Nombre.value);
                          } else if (e.key === 'Escape') {
                            setEditState(prev => ({ ...prev, Nombre: { ...prev.Nombre, editing: false } }));
                          }
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="APDP-field-value">
                      {profesor.Nombre || 'Sin nombre'}
                    </div>
                  )}
                </div>

                {/* Apellido */}
                <div className="APDP-field-item">
                  <div className="APDP-field-header">
                    <label className="APDP-field-label">Apellido *</label>
                    {hasEditProfesores && !editState.apellido.editing && (
                      <button
                        type="button"
                        className="APDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, apellido: { ...prev.apellido, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.apellido.editing ? (
                    <div className="APDP-field-edit">
                      <input
                        type="text"
                        className="APDP-field-input"
                        value={editState.apellido.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          apellido: { ...prev.apellido, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('apellido', editState.apellido.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveField('apellido', editState.apellido.value);
                          } else if (e.key === 'Escape') {
                            setEditState(prev => ({ ...prev, apellido: { ...prev.apellido, editing: false } }));
                          }
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="APDP-field-value">
                      {profesor.apellido || 'Sin apellido'}
                    </div>
                  )}
                </div>

                {/* Especialidad */}
                <div className="APDP-field-item">
                  <div className="APDP-field-header">
                    <label className="APDP-field-label">Especialidad</label>
                    {hasEditProfesores && !editState.Especialidad.editing && (
                      <button
                        type="button"
                        className="APDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, Especialidad: { ...prev.Especialidad, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.Especialidad.editing ? (
                    <div className="APDP-field-edit">
                      <input
                        type="text"
                        className="APDP-field-input"
                        value={editState.Especialidad.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          Especialidad: { ...prev.Especialidad, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('Especialidad', editState.Especialidad.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveField('Especialidad', editState.Especialidad.value);
                          } else if (e.key === 'Escape') {
                            setEditState(prev => ({ ...prev, Especialidad: { ...prev.Especialidad, editing: false } }));
                          }
                        }}
                        placeholder="Ej: Odontología, Ortodoncia, etc."
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="APDP-field-value">
                      {profesor.Especialidad || 'Sin especialidad'}
                    </div>
                  )}
                </div>

                {/* Descripción */}
                <div className="APDP-field-item APDP-field-full-width">
                  <div className="APDP-field-header">
                    <label className="APDP-field-label">Descripción</label>
                    {hasEditProfesores && !editState.Descripcion.editing && (
                      <button
                        type="button"
                        className="APDP-field-edit-btn"
                        onClick={() => setEditState(prev => ({ ...prev, Descripcion: { ...prev.Descripcion, editing: true } }))}
                      >
                        <i className="bi-pencil" />
                      </button>
                    )}
                  </div>
                  {editState.Descripcion.editing ? (
                    <div className="APDP-field-edit">
                      <textarea
                        className="APDP-field-textarea"
                        rows={5}
                        value={editState.Descripcion.value}
                        onChange={(e) => setEditState(prev => ({
                          ...prev,
                          Descripcion: { ...prev.Descripcion, value: e.target.value },
                        }))}
                        onBlur={() => handleSaveField('Descripcion', editState.Descripcion.value)}
                        placeholder="Descripción del profesor..."
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div className="APDP-field-value">
                      {profesor.Descripcion || 'Sin descripción'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Image URL Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Foto del Profesor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>URL de la Foto</Form.Label>
            <Form.Control
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://ejemplo.com/foto.jpg"
              disabled={imageLoading}
            />
            {imageUrl && (
              <div className="text-center mt-3">
                <img
                  src={resolveImageUrl(imageUrl)}
                  alt="Preview"
                  style={{ maxHeight: '200px', maxWidth: '100%', objectFit: 'contain', borderRadius: '0.5rem' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImageModal(false)} disabled={imageLoading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              setImageLoading(true);
              try {
                await handleSaveField('Foto', imageUrl);
                setShowImageModal(false);
                setImageUrl('');
              } catch (error) {
                // Error ya manejado
              } finally {
                setImageLoading(false);
              }
            }}
            disabled={imageLoading}
          >
            {imageLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Guardando...
              </>
            ) : (
              <>
                <i className="bi-check-circle me-2" />
                Guardar Foto
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminProfesorDetallePage = () => (
  <AdminNotificationsProvider>
    <AdminProfesorDetalleInner />
  </AdminNotificationsProvider>
);

export default AdminProfesorDetallePage;

